import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Loader2, Filter } from "lucide-react";

/**
 * FollowupsPanel — used by Hospital Admin ("Follow-ups" tab) to view and
 * complete follow-ups scheduled by the hospital's doctors.
 *
 * Props:
 *   api: authenticated axios instance
 */
export default function FollowupsPanel({ api }) {
  const [status, setStatus] = useState("pending");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const { data } = await api.get(`/dashboard/admin/followups?${params.toString()}`);
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, from, to]);

  const complete = async (id) => {
    setCompleting(id);
    try {
      await api.post(`/followups/${id}/complete`);
      toast.success("Marked as completed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setCompleting(null); }
  };

  return (
    <Card className="medical-card p-4 sm:p-5" data-testid="admin-followups-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-emerald-600" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">Follow-ups</h2>
        </div>
        <Badge variant="outline" className="text-slate-600">{rows.length} results</Badge>
      </div>

      <div className="grid sm:grid-cols-4 gap-2 mb-3 items-end">
        <div className="sm:col-span-2">
          <Tabs value={status} onValueChange={setStatus}>
            <TabsList className="grid grid-cols-4 h-9">
              <TabsTrigger value="pending" data-testid="fu-tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="booked" data-testid="fu-tab-booked">Booked</TabsTrigger>
              <TabsTrigger value="completed" data-testid="fu-tab-completed">Completed</TabsTrigger>
              <TabsTrigger value="all" data-testid="fu-tab-all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1 mb-0.5"><Filter className="w-3 h-3" /> From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="fu-filter-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 font-semibold mb-0.5 block">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="fu-filter-to" />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-3">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="fu-empty">No follow-ups in this filter.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left py-2 px-2 font-medium">Patient</th>
                <th className="text-left py-2 px-2 font-medium">Doctor</th>
                <th className="text-left py-2 px-2 font-medium">Follow-up date</th>
                <th className="text-left py-2 px-2 font-medium">Note</th>
                <th className="text-left py-2 px-2 font-medium">Status</th>
                <th className="text-left py-2 px-2 font-medium">Reminded?</th>
                <th className="text-right py-2 px-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fu-row-${r.id}`}>
                  <td className="py-2 px-2 font-semibold text-slate-800">{r.patient_name}<span className="text-xs text-slate-400 ml-1 font-normal">· {r.mobile}</span></td>
                  <td className="py-2 px-2 text-slate-700">{r.doctor_name}</td>
                  <td className="py-2 px-2 font-mono text-slate-700">{r.followup_date}</td>
                  <td className="py-2 px-2 text-slate-500 max-w-xs truncate" title={r.followup_note || ""}>{r.followup_note || "—"}</td>
                  <td className="py-2 px-2"><StatusPill s={r.status} /></td>
                  <td className="py-2 px-2 text-xs text-slate-500">{r.reminded_at ? "✓ sent" : "—"}</td>
                  <td className="py-2 px-2 text-right">
                    {r.status !== "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => complete(r.id)}
                        disabled={completing === r.id}
                        className="gap-1 h-7 text-xs"
                        data-testid={`fu-complete-${r.id}`}
                      >
                        {completing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Complete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function StatusPill({ s }) {
  const map = {
    pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" },
    booked: { cls: "bg-sky-50 text-sky-700 border-sky-200", label: "Booked" },
    completed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Completed" },
    missed: { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Missed" },
  };
  const m = map[s] || map.pending;
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${m.cls}`}>{m.label}</span>;
}
